import { WorkingCenterModelType } from '@teinor/erp/types/company/workingCenter'
import { connect, ConnectedProps } from 'react-redux'
import { ComponentWithPermissions } from '../../../../../../../../../baseComponents/CreateRoutering/types'
import SingleFormLayout from '../../../../../../../../../baseComponents/Layout/SingleFormLayout'
import { FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import { getDictionaryStates } from '../../../../../../../../../utils/globals/countries'
import { isValidDay } from '../../../../../../../../../utils/globals/numbers'
import { returnClassOfSpanTemplate } from '../../../../../../../../../utils/globals/tailwind'
import { valueOrDefaultValue } from '../../../../../../../../../utils/globals/valueOrDefaultValue'
import withLoading from '../../../../../../../../../utils/hoc/withLoading'
import useCountryState from '../../../../../../../../../utils/hooks/useCountry'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { useTypeIdNumber } from '../../../../../../../../../utils/hooks/useTypeIdNumber'
import { AllReduxPayloads } from '../../../../../../../../../utils/reducers'
import { BillingGroupPayload } from '../../../../../../../../../utils/reducers/company/billingGroups'
import { WorkingCenterTypePayload } from '../../../../../../../../../utils/reducers/company/workingCenterTypes'
import { getActiveCurrenciesDictionary, getActiveLangs, getDictionaryCountries } from '../../../../../../../../../utils/reducers/getters'
import { WorkingCenterModelTypeExtended } from '../../../types'
import { parseSelectedWorkingCenterType } from './function'
import { sortCountries } from '../../../../../../../../../utils/globals/sortCountries'
import { Fragment } from 'react'
import useEditDeleteModal from '../../../../../../../../../utils/hooks/useEditDeleteModal'
import { ButtonSecondary } from '../../../../../../../../../baseComponents/Button'
import ModalConfigWCTemplatesPDF from './ModalConfigWCTemplatesPDF'
import TwinTrans from '../../../../../../../../../baseComponents/TwinTrans'
import { addDictionaryExtraLine, TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { useMemo } from 'react'
import { WorkingCenterPayload } from '../../../../../../../../../utils/reducers/company/workingCenters'
import { emptyExtraLineFirstSortFunction } from '../../../../../../../../../forms/CustomSelect/functions'
import { dictTimeZones } from '@teinor/erp/types/globals/timeZone'

interface MainWorkingCenterProps extends ComponentWithPermissions {
    basicData: WorkingCenterModelType | null
    getData: () => Promise<void>
}

const MainWorkingCenter: React.FC<MainWorkingCenterProps & ReduxWorkingCenterTypes> = ({ basicData, userPermissions, getData, workingCenterTypes, billingGroups, workingCenters }) => {
    const { t } = useTwinTranslation()
    const { typesIdNumber } = useTypeIdNumber()
    const { country, setCountry } = useCountryState({ defaultCountry: basicData?.country })
    const { openModal, setOpenModal } = useEditDeleteModal()
    const dictVatZone: TwinDictionary = {
        '01': {name: t('Spain', 'España')},
        '02': {name: 'Ceuta / Melilla'},
        '03': {name: t('Canary Islands', 'Islas Canarias')}
    }

    const selectedWorkingCenterTypes = parseSelectedWorkingCenterType(basicData?.WorkingCenterTypes || [])
    const dictWorkingCenters = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(workingCenters || {}))), [workingCenters])
    const fields: FormRender<WorkingCenterModelTypeExtended> = [
        {
            cols: 2,
            title: t('generalInformation', 'Información general'),
            elements: [

                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                {
                    name: 'code',
                    label: t('code', 'Código'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'type_id_number',
                    label: t('typeIdNumber', 'Tipo de documento identificación'),
                    component: 'CustomSelect',
                    items: typesIdNumber,
                    value: basicData?.type_id_number
                },
                {
                    name: 'id_number',
                    label: t('id_number', 'DNI'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'BillingGroupId',
                    label: t('billingGroup', 'Grupo de facturación'),
                    component: 'CustomSelect',
                    items: billingGroups?.dictionaryBillingGroup || {},
                },
                {
                    name: 'vatZoneCode',
                    label: t('vatZone', 'Zona de facturación'),
                    component: 'CustomSelect',
                    items: dictVatZone,
                },
                {
                    name: 'surcharge',
                    label: t('surcharge', 'Sobrecargo'),
                    component: 'CustomSelect',
                    items: {
                        false: { name: t('withoutSurcharge', 'Sin sobrecargo')},
                        true: { name: t('withSurcharge', 'Con sobrecargo')}
                    }
                },
                {
                    name: 'BillingWorkingCenterId',
                    label: t('useOtherBillingWorkingCenterForFacturation', 'Usar otro centro de trabajo para facturación'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictWorkingCenters || {},
                    sortFunction: emptyExtraLineFirstSortFunction
                },
                {
                    name: 'CounterWorkingCenterId',
                    label: t('useOtherWorkingCenterCounter', 'Usar otro contador de otro centro de trabajo'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictWorkingCenters || {},
                    sortFunction: emptyExtraLineFirstSortFunction
                },
                {
                    name: 'LangId',
                    label: t('language', 'Idioma'),
                    component: 'CustomSelectWithSearchBar',
                    items: getActiveLangs(),
                    value: basicData?.LangId
                },
                {
                    name: 'CurrencyId',
                    label: t('currency', 'Moneda'),
                    component: 'CustomSelect',
                    items: getActiveCurrenciesDictionary(),
                    value: basicData?.CurrencyId,
                    required: true
                },
                {
                    name: 'timeZone',
                    label: t('timeZone', 'Zona Horaria'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictTimeZones,
                },
                {
                    name: 'validate_deposits',
                    label: t('validateDeposits', 'Validar depósitos'),
                    component: 'CheckboxMargin',
                },
                {
                    name: 'stock_valid',
                    label: t('stocksValid', 'Validar Stock'),
                    component: 'CheckboxMargin',
                },
                {
                    name: 'active_tpv',
                    label: t('activeForTpv', 'Activar para TPV'),
                    component: 'CheckboxMargin',
                },
                {
                    name: 'modalPDF',
                    component: 'Custom',
                    render: () => <div className={returnClassOfSpanTemplate(2)}><ButtonSecondary type='button' onClick={() => setOpenModal({ 'type': 'edit', allRowData: { basicData } })}><TwinTrans transKey='configPdfTemplates'>Configurar plantillas PDF</TwinTrans></ButtonSecondary ></div>,
                },
                {
                    name: 'workingCenterTypes',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('typeWorkingCenter', 'Tipo de Centro de trabajo'), subtitleSelectedOptions: t('typeWorkingCenterSelected', 'Tipo de Centro de trabajo seleccionado')}, 
                    items: workingCenterTypes || {},
                    defaultValue: selectedWorkingCenterTypes,
                    className: returnClassOfSpanTemplate(2)
                },
            ]
        },
        {
            cols: 2,
            title: t('contactInformation', 'Datos de contacto'),
            elements: [
                {
                    name: 'phone',
                    label: t('phone', 'Teléfono'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'mobile',
                    label: t('mobile', 'Móvil'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'email',
                    label: t('email', 'Email'),
                    component: 'InputWithLabelMargin',
                    type: 'email',
                },
                {
                    name: 'id',
                    component: 'InputHidden'
                }
            ]
        },
        {
            cols: 2,
            title: t('address', 'Dirección'),
            elements: [
                {
                    name: 'address',
                    label: t('address', 'Dirección'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'additional_address',
                    label: t('additional_address', 'Dirección adicional'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'postal_code',
                    label: t('postal_code', 'Código Postal'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'city',
                    label: t('city', 'Ciudad'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'country',
                    label: t('country', 'País'),
                    component: 'CustomSelectWithSearchBar',
                    items: getDictionaryCountries(),
                    sortFunction: sortCountries,
                    value: country,
                    onChange: (value: string) => setCountry(value),
                    required: true
                },
                {
                    name: 'state',
                    label: t('state', 'Provincia'),
                    component: 'CustomSelectWithSearchBar',
                    sortItems: true,
                    items: getDictionaryStates(country),
                    value: basicData?.state,
                },
            ]
        },
        {
            cols: 1,
            title: t('payDays', 'Días de pago'),
            elements: [
                {
                    name: 'payDays',
                    component: 'ListAdderStateFull',
                    label: t('payDays', 'Días de pago'),
                    items: basicData?.['payDays'] || [],
                    checkValid: isValidDay
                }
            ]
        }
    ]
    const parsedFields = valueOrDefaultValue(fields, userPermissions, basicData)

    return (
        <Fragment>
            <SingleFormLayout action='/api/app/workingCenter/updateWorkingCenter' onSubmit={getData} sections={parsedFields} userPermissions={userPermissions} />
            {openModal?.type === 'edit' ? <ModalConfigWCTemplatesPDF basicData={basicData || {}} onSubmit={getData} setOpened={() => setOpenModal(null)} /> : null }
        </Fragment>
    )
}

const workingCenterTypesDispatch = {
    setWorkingCenterTypes: (payload: WorkingCenterTypePayload) => ({ type: 'CHANGE_WORKINGCENTERTYPE', payload }),
    setBillingGroups: (payload: BillingGroupPayload) => ({ type: 'CHANGE_BILLINGGROUP', payload }),
    setWorkingCenters: (payload: WorkingCenterPayload) => ({ type: 'CHANGE_WORKINGCENTER', payload }),
}

export type ReduxWorkingCenterTypes = ConnectedProps<typeof workingCenterTypesConnect>
const mapWorkingCenterTypesConnector = (state: AllReduxPayloads) => ({ workingCenterTypes: state.workingCenterTypes, billingGroups: state.billingGroups, workingCenters: state.workingCenters })
const workingCenterTypesConnect = connect(mapWorkingCenterTypesConnector, workingCenterTypesDispatch)

const workingCenterTypesConnectLoading = withLoading(MainWorkingCenter, [{ fetchUrl: '/api/app/workingCenter/workingCenterType/getAllWorkingCenterTypes', propName: 'workingCenterTypes', setFunctionName: 'setWorkingCenterTypes' }, { fetchUrl: '/api/app/config/billingGroup/getAllBillingGroups', propName: 'billingGroups', setFunctionName: 'setBillingGroups' }, { fetchUrl: '/api/app/workingCenter/getAllWorkingCentersComplete', propName: 'workingCenters', setFunctionName: 'setWorkingCenters' }])

const MainWorkingCenterConnect = workingCenterTypesConnect(workingCenterTypesConnectLoading)

export default MainWorkingCenterConnect